'use client';

import { ChangeEvent, ComponentProps, useState } from 'react';

import { noop, useDebouncedCallback } from '@northladder/utilities';
import { cn } from '../lib';

/**
 * Configuration properties for the Input component.
 */
type TInput = ComponentProps<'input'> & {
  /**
   * The duration in microseconds over which the callback has to wait.
   * - Default is `300` milliseconds.
   */
  delay?: number;
  /** Callback function triggered when the input value changes, after the specified delay. */
  onChangeDebounced?: (text: string) => void;
};

/**
 * A styled input component based on shadcn UI.
 */
function Input({
  className,
  type,
  delay = 300,
  onChange,
  onChangeDebounced,
  value,
  ...props
}: TInput) {
  const [text, setText] = useState(value);

  const handleInputChangeDebounced = useDebouncedCallback(
    onChangeDebounced || noop,
    {
      wait: delay,
    }
  );

  const handleOnInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setText(e.target.value);

    if (onChangeDebounced) {
      handleInputChangeDebounced(newValue);
    } else if (onChange) {
      onChange(e);
    }
  };

  return (
    <input
      {...props}
      className={cn(
        `file:text-foreground aria-invalid:ring-destructive/20
        dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive
        disabled:bg-input-background-disabled flex min-w-0 px-3 py-1 text-base
        transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0
        file:bg-transparent file:text-sm file:font-medium md:text-sm`,
        className
      )}
      data-slot="input"
      onChange={handleOnInputChange}
      type={type}
      value={text}
    />
  );
}

export { Input };
